import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LOGIN, AUTH } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { formatApiErrorDetail } from '@/lib/api';
import MathCaptcha from '@/components/MathCaptcha';
import { Sparkles, Loader2, Mail, Lock, Eye, EyeOff } from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [captcha, setCaptcha] = useState({ captcha_id: '', answer: '' });
  const captchaRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(email, password, { captcha_id: captcha.captcha_id, captcha_answer: captcha.answer });
      navigate('/dashboard');
    } catch (err) {
      console.error('[Maarivil] login failed:', err);
      setError(formatApiErrorDetail(err.response?.data?.detail, err));
      captchaRef.current?.refresh();
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 left-1/3 h-96 w-96 rounded-full bg-fuchsia-500/20 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/3 h-96 w-96 rounded-full bg-cyan-500/20 blur-3xl" />
      </div>
      <div className="w-full max-w-md glass-strong rounded-3xl border border-white/10 p-10">
        <div className="flex justify-center mb-6">
          <img src="/maarivil-logo.png" alt="Maarivil" className="h-16 w-auto object-contain drop-shadow-[0_0_20px_rgba(217,70,239,0.3)]" />
        </div>
        <h1 className="font-display text-3xl font-bold text-center text-white mb-2">Welcome back</h1>
        <p className="text-slate-400 text-center mb-8 text-sm">Sign in to continue your journey on <span className="rainbow-text font-medium">Maarivil</span></p>

        <button
          onClick={googleLogin}
          data-testid={AUTH.googleButton}
          className="w-full glass border border-white/10 hover:border-fuchsia-400/40 transition rounded-xl py-3 text-white flex items-center justify-center gap-2 mb-6"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-slate-500 uppercase tracking-widest">or email</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Email</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                value={email} onChange={(e) => setEmail(e.target.value)}
                type="email" required data-testid={LOGIN.emailInput}
                className="bg-white/5 border-white/10 pl-9"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Password</Label>
              <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-xs text-fuchsia-300 hover:text-fuchsia-200 transition-colors">
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                value={password} onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? 'text' : 'password'} required data-testid={LOGIN.passwordInput}
                className="bg-white/5 border-white/10 pl-9 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                data-testid="login-password-toggle"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <MathCaptcha ref={captchaRef} onChange={setCaptcha} />
          {error && <div className="text-rose-300 text-sm bg-rose-500/10 border border-rose-500/30 rounded-lg p-3">{error}</div>}
          <Button type="submit" disabled={loading} data-testid={LOGIN.submitButton} className="w-full btn-rainbow rounded-xl py-6 text-base text-white border-0">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in…</> : 'Sign in'}
          </Button>
        </form>

        <p className="text-center text-sm text-slate-400 mt-6">
          New here?{' '}
          <Link to="/register" data-testid={LOGIN.registerLink} className="text-fuchsia-300 hover:text-fuchsia-200">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
